import {
  useMemo,
  useRef,
  type ReactNode,
  Children,
  isValidElement,
  cloneElement,
} from 'react';

import { NUMBER_SPIN_BUTTON_DEFAULTS } from '../../number-spin-button/constants.js';
import { isNumberSpinButton } from '../../number-spin-button/NumberSpinButton.js';
import {
  NumberSpinButtonProps,
  NumberSpinButtonRef,
} from '../../number-spin-button/types.js';
import { isTextSpinButton } from '../../text-spin-button/TextSpinButton.js';
import {
  TextSpinButtonProps,
  TextSpinButtonRef,
} from '../../text-spin-button/types.js';
import { isSpinButtonGroupSeparator } from '../SpinButtonGroupSeparator.js';
import type {
  ChildWithRef,
  SpinButtonGroupSeparatorProps,
  SpinButtonHandler,
} from '../types.js';

/**
 * Function to handle a potential value overflow from the `current` spinbutton to the `target` spinbutton.
 * A value overflow could be either if
 * * the candidate numeric value is higher than the allowed maxValue
 * * the candidate length is exceeding the max nr of digits for the current spinbutton
 */
function handleValueOverflow(
  candidate: string,
  event: InputEvent,
  maxValue: number,
  current: SpinButtonHandler,
  target: SpinButtonHandler,
) {
  // Consider the last char as overflow
  const overflowChar = candidate.substring(candidate.length - 1);
  const isNumericOverflow = Number.isInteger(parseInt(overflowChar));

  const candidateValue = parseInt(candidate);
  const maxNrOfDigits = maxValue.toString().length;
  const currentText = current?.element?.current?.textContent ?? '';

  // Only overflow if selection is collapsed and cursor is at the last position.
  const sel = current?.getSelection();
  if (
    sel === undefined ||
    sel.start !== sel.end ||
    sel.end !== currentText.length
  ) {
    return;
  }

  // Nothing to do if the candidate value is smaller and shorter than the allowed max value.
  if (
    isNumericOverflow &&
    candidate.length <= maxNrOfDigits &&
    candidateValue <= maxValue
  ) {
    return;
  }

  // Handle overflow based on the target spinbutton
  switch (target?.type) {
    case 'number':
      // Only allow a numeric overflow in this case
      if (!isNumericOverflow) {
        return;
      }

      // Update target content, and set cursor to last target position
      target?.applyText(overflowChar, true);
      target?.element.current?.focus();
      target?.setSelection({
        start: 0,
        end: 0,
        rtl: true,
      });
      break;
    case 'text':
      // Search for a text value in the target spin button and focus it
      target?.searchValue(overflowChar, true);
      target?.element.current?.focus();
      break;
  }

  // prevent input processing for the current spinbutton
  event.preventDefault();
}

/**
 * Hook to provide ootb value overflow behavior for spinbuttons within the group.
 * To achieve this, the provided children needs to be cloned to get access to the imperative handlers.
 * In addition to the spin button handlers, it provides an array of trigger refs.
 */
export function useValueOverflow(children: ReactNode, valueOverflow: boolean) {
  const spinButtonRefs = useRef<SpinButtonHandler[]>([]);
  const triggerRefs = useRef<string[]>([]);

  // Extract, store and link potential external ref to the current ref.
  function linkRefAndRemember<T extends SpinButtonHandler>(
    child: ReactNode,
    index: number,
    handler: T,
  ) {
    // Remember component handler for ffu.
    spinButtonRefs.current[index] = handler;
    const { ref } = child as ChildWithRef<T>;

    // Nothing else to do if no external ref was passed.
    if (ref === undefined || ref === null) {
      return;
    }

    // Execute callback ref or forward handler to external ref.
    if (typeof ref === 'function') {
      ref(handler);
    } else {
      ref.current = handler;
    }
  }

  // Cloned children with extended ootb functionality
  const clonedChildren = useMemo(
    () =>
      Children.map(children, (child, index) => {
        if (!isValidElement(child)) {
          return child;
        }

        if (isNumberSpinButton(child)) {
          return cloneElement<
            NumberSpinButtonProps & {
              ref?: (handler: NumberSpinButtonRef) => void;
            }
          >(
            child,
            {
              ...child.props,
              ref: (handler: NumberSpinButtonRef) =>
                linkRefAndRemember(child, index, handler),
              onBeforeInput: (candidate, event) => {
                const {
                  maxValue = NUMBER_SPIN_BUTTON_DEFAULTS.MAX_VALUE,
                  onBeforeInput,
                } = child.props as NumberSpinButtonProps;

                // Call an external handler if one is set, exit early if necessary.
                onBeforeInput?.(candidate, event);
                if (event.defaultPrevented) {
                  return;
                }

                // Search for the next configured spinbutton within the group
                const nextSpinButton = spinButtonRefs.current.find(
                  (handler, i) => handler && i > index,
                );

                // If there is a next spinbutton and value overflow is enabled, handle the overflow
                valueOverflow &&
                  nextSpinButton &&
                  candidate !== '' &&
                  handleValueOverflow(
                    candidate,
                    event,
                    maxValue,
                    spinButtonRefs.current[index],
                    nextSpinButton,
                  );
              },
            },
            children,
          );
        } else if (isTextSpinButton(child)) {
          return cloneElement<
            TextSpinButtonProps & { ref?: (handler: TextSpinButtonRef) => void }
          >(
            child,
            {
              ...child.props,
              ref: (handler: TextSpinButtonRef) =>
                linkRefAndRemember(child, index, handler),
            },
            children,
          );
        } else if (isSpinButtonGroupSeparator(child)) {
          // Remember the trigger key for the current child index
          triggerRefs.current[index] = (
            child.props as SpinButtonGroupSeparatorProps
          ).trigger;
          return child;
        }

        return child;
      }),
    [children, valueOverflow],
  );

  return {
    clonedChildren,
    spinButtonRefs,
    triggerRefs,
  };
}
